// Google Analytics utilities for PinkSpider platform

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}

export const GA_TRACKING_ID = 'G-HETRJP46LD';

export type MarketingAttribution = {
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmContent: string;
  utmTerm: string;
  landingPage: string;
  referrer: string;
};

const ATTRIBUTION_KEY = 'pinkspider_marketing_attribution';

const safeReferrer = (value: string): string => {
  if (!value) return '';
  try {
    const url = new URL(value);
    return `${url.origin}${url.pathname}`;
  } catch {
    return '';
  }
};

export const captureAttribution = (): MarketingAttribution => {
  if (typeof window === 'undefined') {
    return { utmSource: '', utmMedium: '', utmCampaign: '', utmContent: '', utmTerm: '', landingPage: '', referrer: '' };
  }
  const params = new URLSearchParams(window.location.search);
  const current = {
    utmSource: params.get('utm_source') || '',
    utmMedium: params.get('utm_medium') || '',
    utmCampaign: params.get('utm_campaign') || '',
    utmContent: params.get('utm_content') || '',
    utmTerm: params.get('utm_term') || '',
    landingPage: window.location.pathname,
    referrer: safeReferrer(document.referrer),
  };
  const hasUtm = Boolean(current.utmSource || current.utmMedium || current.utmCampaign || current.utmContent || current.utmTerm);
  try {
    const stored = JSON.parse(sessionStorage.getItem(ATTRIBUTION_KEY) || 'null') as MarketingAttribution | null;
    const attribution = hasUtm || !stored ? current : stored;
    sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attribution));
    return attribution;
  } catch {
    return current;
  }
};

export const getAttribution = (): MarketingAttribution => captureAttribution();

// Track page views: called on every SPA route change via App.tsx PageTracker.
// index.html uses send_page_view: false so GA does not auto-fire the initial
// page_view; this function is the single source of truth for all page views.
export const trackPageView = (url: string, title?: string) => {
  captureAttribution();
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'page_view', {
      page_title: title,
      page_location: url,
      page_path: new URL(url).pathname,
    });
  }
};

// Track custom events
export type MarketingEventName =
  | 'marketing_page_view'
  | 'demo_cta_click'
  | 'demo_form_start'
  | 'demo_form_submit'
  | 'demo_form_error'
  | 'product_page_view'
  | 'resource_view'
  | 'resource_cta_click'
  | 'login_click'
  | 'outbound_integration_click'
  | 'audience_card_click'
  | 'industry_teaser_click'
  | 'home_resource_card_click'
  | 'nav_dropdown_open'
  | 'nav_item_click'
  | 'demo_sample_content_provided'
  | 'works_with_click'
  | 'better_together_click'
  | 'product_related_click'
  | 'industry_card_click'
  | 'industry_product_click'
  | 'industry_source_click'
  | 'industry_cta_click'
  | 'resource_category_click'
  | 'resource_type_view'
  | 'resource_related_click'
  | 'regulatory_library_view'
  | 'template_checklist_view'
  | 'automations_demo_click'
  | 'post_check_submit'
  | 'product_update_view'
  | 'product_update_related_click'
  | 'product_update_external_click'
  | 'footer_link_click'
  | 'outbound_link_click'
  | 'mailto_click'
  | 'download_click'
  | 'page_not_found'
  | 'unsubscribe_submit';

// Extra event parameters beyond category/label/value, following the same
// flat, non-sensitive convention (page paths, product/category names, source
// publishers). Never pass free-text or user-entered content here.
export type MarketingEventParams = Record<string, string | number | undefined>;

export const trackEvent = (
  action: MarketingEventName,
  category: string,
  label?: string,
  value?: number,
  extraParams?: MarketingEventParams
) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
      ...extraParams,
    });
  }
};

export const trackFormSubmission = (formName: 'demo_request', success: boolean) => {
  trackEvent(success ? 'demo_form_submit' : 'demo_form_error', 'form', formName, 1);
};
